import React, { useState } from 'react'
import { exportPolicyRenewals, getBrokerDashboardBrokerPerformance, getBrokerDashboardChartMonthlyMetrics, getBrokerDashboardCustomerRetentionMetrics, getBrokerDashboardInsuranceMetrics, getBrokerDashboardMetricsSummary, getBrokerDashboardStaffPerformance, getRevenueTarget, getRevenueTrendWithTarget } from '../api/requests';
import { IMonthlyChartMetrics } from '../views/shared/dashboard/broker/components/SalesTrendsChart';
import { getMonthNameFromDate, showToast } from '../utils/miscFunctions';
import CreateRevenueTarget from '../views/shared/dashboard/shared/revenueTarget/CreateRevenueTarget';
import useModal from './modalHook';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { INPUT_DATE_FORMAT } from '../resources/constants';

interface IBrokerDashboardMetricsSummary {
  activeAgentPercentage: number;
  activeAgents: number;
  commissionChangePercent: number;
  commissionEarned: number;
  monthlySales: number;
  monthlySalesChangePercent: number;
  targetCompletionRate: number;
  targetCompletionRateChange: number;
  totalAgents: number;
  yearToDateSales: number;
  yearToDateCommission: number;
}
interface IInsuranceMetricsData {
  coverageAnalysis: {
    totalCoverageValue: number;
    averagePremiums: {
      [key: string]: number;
    };
    coverageDistribution: {
      [key: string]: number;
    };
  };
  policyLifecycle: {
    newPolicies: number;
    expiredPolicies: number;
    totalPolicies: number;
    activePolicies: number;
    maturePolicies: number;
    retentionRate: number;
  };
  claimsImpact: {
    averageClaimAmount: number;
    claimsRatio: number;
    totalClaims: number;
    claimsTrend: string;
  };
  renewalMetrics: {
    upcomingRenewalsPremium: number;
    lostBusinessesPremium: number;
    lostBusinessesCount: number;
    dueForRenewalPremium: number;
    renewalRate: number;
    dueForRenewalCount: number;
    renewedPoliciesCount: number;
    renewedPoliciesPremium: number;
    upcomingRenewalsCount: number;
  };
}
interface IRevenueTarget {
  achievementPercentage: number;
  branchId?: string;
  branchName?: string;
  companyId?: string;
  companyName?: string;
  currentAmount: number;
  month: string;
  targetAmount: number;
  year: number;
}
const useBroker = () => {
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const BrokerModal = ModalComponent;
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [brokerMetricsSummaryData, setBrokerMetricsSummaryData] = useState<IBrokerDashboardMetricsSummary>({
    activeAgentPercentage: 100,
    activeAgents: 1,
    commissionChangePercent: 0,
    commissionEarned: 0,
    monthlySales: 0,
    monthlySalesChangePercent: 0,
    targetCompletionRate: 0,
    targetCompletionRateChange: 0,
    totalAgents: 1,
    yearToDateSales: 0,
    yearToDateCommission: 0
  });

  const [insuranceMetrics, setInsuranceMetrics] = useState<IInsuranceMetricsData>({
    coverageAnalysis: {
      totalCoverageValue: 0,
      averagePremiums: {},
      coverageDistribution: {}
    },
    policyLifecycle: {
      newPolicies: 0,
      expiredPolicies: 0,
      totalPolicies: 0,
      activePolicies: 0,
      maturePolicies: 0,
      retentionRate: 0
    },
    claimsImpact: {
      averageClaimAmount: 0,
      claimsRatio: 0,
      totalClaims: 0,
      claimsTrend: ''
    },
    renewalMetrics: {
      upcomingRenewalsPremium: 0,
      lostBusinessesPremium: 0,
      lostBusinessesCount: 0,
      dueForRenewalPremium: 0,
      renewalRate: 0,
      dueForRenewalCount: 0,
      renewedPoliciesCount: 0,
      renewedPoliciesPremium: 0,
      upcomingRenewalsCount: 0
    }
  });

  const [monthlyChartMetrics, setMonthlyChartMetrics] = useState<IMonthlyChartMetrics>({
    salesAmounts: [],
    commissionAmounts: [],
    revenueTargets: [],
    months: []
  });

  const [customerMetricsData, setCustomerMetricsData] = useState({
    customerValue: {
      topCustomerValues: [0, 0, 0, 0, 0],
      averageCustomerValue: 0,
      totalCustomerValue: 0,
      customerCount: 0
    },
    acquisition: {
      totalCustomers: 0,
      newCustomersThisMonth: 0,
      newCustomersThisYear: 0,
      acquisitionRate: 0
    },
    satisfaction: {
      trend: '',
      satisfactionScore: 0,
      responseRate: 0,
      npsScore: 0
    },
    retention: {
      customersLastYear: 0,
      retainedCustomers: 0,
      activeCustomers: 0,
      retentionRate: 0
    }
  });

  const [revenueTargetData, setRevenueTargetData] = useState<IRevenueTarget>({
    achievementPercentage: 0,
    currentAmount: 0,
    month: '',
    targetAmount: 0,
    year: 0
  });

  const [revenueTarget, setrevenueTarget] = useState<any>();

  const [brokerPerformance, setBrokerPerformance] = useState<any>();

  const [targetPeriod, setTargetPeriod] = useState<Date>(new Date());

  const getBrokerDashboardMetricsSummaryData = async (year?: string, companyId?: number) => {
    setIsLoading(true)
    try {
      const res: any = await getBrokerDashboardMetricsSummary(year, companyId);
      if (res) {
        setBrokerMetricsSummaryData(res?.data);

      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false)
    }
  };

  const getInsuranceMetrics = async (companyId?: number) => {
    try {
      const response: any = await getBrokerDashboardInsuranceMetrics(companyId);

      if (response) {
        setInsuranceMetrics(response.data);
      }
    } catch (error) { }
  };

  const getDashboardChartMonthlyMetrics = async (year?: number, companyId?: number) => {
    try {
      const res: any = await getBrokerDashboardChartMonthlyMetrics(year, companyId);
      if (res) {
        setMonthlyChartMetrics(res?.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getBrokerDashboardCustomerMetrics = async () => {
    try {
      const response: any = await getBrokerDashboardCustomerRetentionMetrics();
      if (response) {
        setCustomerMetricsData(response?.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getRevenueTargetData = async () => {
    const year = targetPeriod.getFullYear();
    const month = getMonthNameFromDate(targetPeriod);

    try {
      const res: any = await getRevenueTarget(month, year);
      setRevenueTargetData(res?.data);
    } catch (error: any) {
      if (error?.status === 404) {
        setRevenueTargetData({
          achievementPercentage: 0,
          currentAmount: 0,
          month: '',
          targetAmount: 0,
          year: 0
        });
        // showToast.info(error.description);
      }
      console.error(error);
    }
  };

  const getRevenueTrendData = async (companyId?: number) => {
    try {
      const res: any = await getRevenueTrendWithTarget(companyId);
      setrevenueTarget(res?.data);
    } catch (error: any) {
      console.error(error);
    }
  };

  const fetchBrokerPerformance = async (brokerId?: number, period?: string) => {
    try {
      const res: any = await getBrokerDashboardBrokerPerformance(brokerId, period);
      setBrokerPerformance(res);
    } catch (error: any) {
      console.error(error);
    }
  };

  const fetchBrokerStaffPerformance = async () => {
    try {
      const res: any = await getBrokerDashboardStaffPerformance();
      return (res?.data);
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleCreateRevenueTarget = () => {
    updateModalStates({
      children: (
        <CreateRevenueTarget
          onClose={() => closeModal()}
          onProceed={() => {
            queryClient.invalidateQueries({
              queryKey: ['getRevenueTargetData', targetPeriod]
            });
            closeModal();
          }}
        />
      ),
      show: true,
      static: false,
      size: 'sm'
    });
  };

  const exportFilteredRenewals = async (
    brokerId?: number,
    search?: string,
    expiredFrom?: string,
    expiredTo?: string,
    insurerId?: number,
    policyType?: string,
    renewalState?: string
  ) => {
    const fromDate = expiredFrom ? moment(expiredFrom).format(INPUT_DATE_FORMAT) : undefined
    const toDate = expiredTo ? moment(expiredTo).format(INPUT_DATE_FORMAT) : undefined

    try {
      const res: any = await exportPolicyRenewals(brokerId, search, fromDate, toDate, insurerId, policyType, renewalState)
      if (res) {
        showToast.success(res?.message || "")
        window.open(res.data?.url, '_blank');
      }
    } catch (error: any) {
      showToast.error(error?.description);
    }
  };

  return {
    getBrokerDashboardMetricsSummaryData,
    getBrokerDashboardCustomerMetrics,
    getDashboardChartMonthlyMetrics,
    handleCreateRevenueTarget,
    getRevenueTargetData,
    getRevenueTrendData,
    getInsuranceMetrics,
    fetchBrokerPerformance,
    brokerPerformance,
    fetchBrokerStaffPerformance,
    setTargetPeriod,
    brokerMetricsSummaryData,
    customerMetricsData,
    monthlyChartMetrics,
    revenueTargetData,
    insuranceMetrics,
    revenueTarget,
    targetPeriod,
    isLoading,
    BrokerModal,
    exportFilteredRenewals
  }
}

export default useBroker